/**
 * Holds a breadcrumb trail to a single line on the breakpoint(s) where the
 * "truncate, tap to expand" overflow behaviour is selected.
 *
 * It folds the front of the trail behind a leading ellipsis and reveals as many
 * trailing items as fit:
 *   - `… › Category › Topic`  when the whole thing fits on one line
 *   - `… › Topic`             when it doesn't (Category folds behind the ellipsis)
 *   - `… › Topic…`            when even the deepest item is too wide (it clips)
 *
 * The fit is measured against the live layout and re-measured on resize, so the
 * trail shows the most it can at any width. Tapping the ellipsis expands to the
 * full trail (allowed to wrap) and is one-way.
 *
 * Whether the collapse is active is decided here (not by a CSS media query) so a
 * single class — breadcrumb-block--collapsed — drives the styling, and the same
 * markup serves a breakpoint where the behaviour is off (nothing collapses).
 */
class BreadcrumbCollapse extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.breadcrumb-block__list');
    if (!this.list) return;

    this.block = this.closest('.breadcrumb-block');
    if (!this.block) return;

    // Take over from the CSS first-paint skeleton: from here on the module owns
    // the collapsed/expanded state (via breadcrumb-block--collapsed), so the
    // skeleton must not linger — otherwise it re-folds the trail after expand(),
    // which removes --collapsed. measure() below runs synchronously in this same
    // task, so dropping the skeleton now can't reintroduce the load flash.
    this.block.classList.remove('breadcrumb-block--collapse-pending');

    const items = Array.from(this.list.querySelectorAll(':scope > .breadcrumb-block__item'));
    if (items.length === 0) return;

    this.items = items;
    // The deepest item is always shown (clipped if it alone is too wide); the
    // second-to-last (e.g. Category) is shown only when the trail fits; anything
    // before that is always folded behind the ellipsis.
    this.topic = items[items.length - 1];
    this.category = items.length >= 2 ? items[items.length - 2] : null;
    this.frontFolded = items.slice(0, Math.max(0, items.length - 2));

    if (items.length >= 2) {
      this.ellipsisItem = document.createElement('li');
      this.ellipsisItem.className = 'breadcrumb-block__item breadcrumb-collapse__ellipsis';
      this.toggle = document.createElement('button');
      this.toggle.type = 'button';
      this.toggle.className = 'breadcrumb-block__link breadcrumb-collapse__toggle';
      this.toggle.textContent = '…';
      this.toggle.setAttribute('aria-expanded', 'false');
      const label = this.getAttribute('data-expand-label');
      if (label) this.toggle.setAttribute('aria-label', label);
      this.ellipsisItem.appendChild(this.toggle);
      this.list.insertBefore(this.ellipsisItem, this.list.firstChild);
      this.toggle.addEventListener('click', () => this.expand());
    }

    this.measure = this.measure.bind(this);
    this.resizeObserver = new ResizeObserver(this.measure);
    // Observe the available width (parent), which — unlike the fit-content block
    // itself — doesn't change when we fold items, so measuring can't loop.
    this.resizeObserver.observe(this.block.parentElement || this.block);
    this.mediaQueries = [
      window.matchMedia('(max-width: 749px)'),
      window.matchMedia('(min-width: 750px)'),
    ];
    this.mediaQueries.forEach((mq) => mq.addEventListener('change', this.measure));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);

    this.measure();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.mediaQueries?.forEach((mq) => mq.removeEventListener('change', this.measure));
  }

  #collapseActive() {
    const classes = this.block.classList;
    return (
      (classes.contains('breadcrumb-block--collapse-mobile') &&
        window.matchMedia('(max-width: 749px)').matches) ||
      (classes.contains('breadcrumb-block--collapse-desktop') &&
        window.matchMedia('(min-width: 750px)').matches)
    );
  }

  #overflows() {
    return this.list.scrollWidth - this.list.clientWidth > 1;
  }

  measure() {
    if (this.expanded) return;

    if (!this.#collapseActive()) {
      this.block.classList.remove('breadcrumb-block--collapsed');
      return;
    }
    this.block.classList.add('breadcrumb-block--collapsed');

    // Start from the fullest state: front folded, Category shown, Topic unclipped.
    this.frontFolded.forEach((item) => item.classList.add('breadcrumb-collapse__folded'));
    this.category?.classList.remove('breadcrumb-collapse__folded');
    this.topic.classList.remove('breadcrumb-collapse__clip');
    if (this.ellipsisItem) this.ellipsisItem.hidden = this.frontFolded.length === 0;

    if (this.category) {
      // Does `… › Category › Topic` fit on one line?
      if (this.#overflows()) {
        // No — fold Category down to `… › Topic`.
        this.category.classList.add('breadcrumb-collapse__folded');
        if (this.ellipsisItem) this.ellipsisItem.hidden = false;
        // Still too wide? Clip the Topic's own label with an ellipsis.
        if (this.#overflows()) this.topic.classList.add('breadcrumb-collapse__clip');
      }
    } else if (this.#overflows()) {
      // Single-item trail that's too wide on its own.
      this.topic.classList.add('breadcrumb-collapse__clip');
    }
  }

  expand() {
    this.expanded = true;
    this.block.classList.remove('breadcrumb-block--collapsed');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.items.forEach((item) =>
      item.classList.remove('breadcrumb-collapse__folded', 'breadcrumb-collapse__clip')
    );
    // Drop the toggle so the first item no longer draws a leading separator.
    const firstFolded = this.frontFolded[0] || this.category;
    this.ellipsisItem?.remove();
    firstFolded?.querySelector('a, button')?.focus();
  }
}

if (!customElements.get('breadcrumb-collapse')) {
  customElements.define('breadcrumb-collapse', BreadcrumbCollapse);
}
